import { useEffect, useState } from 'react'
import { CHANGELOG } from '../lib/changelog'

const latest = CHANGELOG[0].id

function read(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function write(key: string, value: string) {
  try {
    localStorage.setItem(key, value)
  } catch {
    // Private window or blocked storage: the dot simply comes back next time.
  }
}

/**
 * Whether the changelog has an entry this person has not opened yet. What they
 * saw last is kept in the browser, per account. A first visit counts as having
 * seen everything: someone new should not be handed the whole history as news.
 */
export function useChangelogSeen(userId: string | undefined): {
  hasUnseen: boolean
  markSeen: () => void
} {
  const key = `changelog-seen:${userId}`
  // Tagged with its key, so another account's value is never read as this one's.
  const [stored, setStored] = useState<{ key: string; id: string } | null>(null)

  useEffect(() => {
    if (!userId) return
    const id = read(key)
    if (id === null) write(key, latest)
    setStored({ key, id: id ?? latest })
  }, [key, userId])

  const seen = stored?.key === key ? stored.id : null
  // Only an older entry of this list is news: an id from a newer build is not.
  const hasUnseen = seen !== null && CHANGELOG.findIndex((entry) => entry.id === seen) > 0

  const markSeen = () => {
    if (!hasUnseen) return
    write(key, latest)
    setStored({ key, id: latest })
  }

  return { hasUnseen, markSeen }
}
